import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class ResizeImageParams {
  @IsUUID()
  @IsNotEmpty()
  attachment_id: string;
}

export class GetAttachmentUrlParams {
  @IsUUID()
  @IsNotEmpty()
  attachment_id: string;
}

export class GetTagsQuery {
  @IsString()
  @IsOptional()
  search?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  account_ids?: string[];

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class ResizeImageQuery {
  @IsInt()
  @IsOptional()
  width?: number;

  @IsInt()
  @IsOptional()
  height?: number;
}

export class Tag {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class TagsResponse {
  @ValidateNested({ each: true })
  @Type(() => Tag)
  @IsArray()
  @IsNotEmpty()
  tags: Tag[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class AttachmentUrl {
  @IsString()
  @IsNotEmpty()
  url: string;
}

export type AttachmentItemType = string;

export class AttachmentItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

export class UploadAttachmentRequest {
  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @Type(() => String)
  @IsNotEmpty()
  file: File;

  @IsIn(['image', 'file'])
  @IsString()
  @IsNotEmpty()
  type: 'image' | 'file';
}

@injectable()
export class ContentService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getTags({
    query = {},
  }: {
    query?: GetTagsQuery;
  } = {}) {
    await Promise.all([this.validator.validateOrReject(query, GetTagsQuery)]);

    let requestPath = '/content/tags';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, TagsResponse);

    return response;
  }

  async resizeImage({
    query = {},
    params,
  }: {
    query?: ResizeImageQuery;
    params: ResizeImageParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, ResizeImageQuery),
      this.validator.validateOrReject(params, ResizeImageParams),
    ]);

    let requestPath = '/attachment/{attachment_id}/image/resize';

    const paramsKeys = Object.keys(params) as (keyof ResizeImageParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: false,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async getAttachmentUrl({ params }: { params: GetAttachmentUrlParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetAttachmentUrlParams),
    ]);

    let requestPath = '/attachment/{attachment_id}/url';

    const paramsKeys = Object.keys(params) as (keyof GetAttachmentUrlParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, AttachmentUrl);

    return response;
  }

  async uploadAttachment({ body }: { body: UploadAttachmentRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, UploadAttachmentRequest),
    ]);

    let requestPath = '/attachment/upload';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      isJson: false,
    });

    const response = await this.unmarshaller.unmarshall(result, AttachmentItem);

    return response;
  }
}
